import client from './client';

/**
 * API client for the Weekly Safety Message admin page (TAS-128).
 *
 * Consumes the super-admin-only endpoints in the API repo's
 * routes/admin/safetyMessages.ts:
 *   GET    /admin/safetyMessages?from=&to=
 *   GET    /admin/safetyMessages/:id
 *   POST   /admin/safetyMessages
 *   PUT    /admin/safetyMessages/:id
 *   DELETE /admin/safetyMessages/:id      (soft delete — Active = 0)
 *
 * THERE IS NO PUBLISH CALL. Saving a message publishes it: POST writes
 * Published = 1 server-side, because the pay period is the schedule and a future
 * week's message cannot reach a phone before its week begins. Do not add a
 * draft/publish split here without revisiting the API — /self only ever returns
 * Published = 1 rows.
 *
 * Shapes mirror the API repo's dbInterfaces/SafetyMessage.ts exactly (camelCase).
 * NOTE the deliberate case mismatch elsewhere on the page: pay periods come from
 * GET /admin/payPeriods, which returns the recordset verbatim and is therefore
 * PascalCase (PayPeriodID / StartDate / EndDate).
 */

/** A row from GET /admin/safetyMessages (API dbInterfaces/SafetyMessage → SafetyMessage). */
export interface SafetyMessage {
  safetyMessageID: number;
  payPeriodID: number;
  /** Joined from PayPeriod on the list/detail reads. ISO date strings over the wire. */
  startDate?: string;
  endDate?: string;
  title: string;
  title_es: string | null;
  body: string;
  body_es: string | null;
  published: boolean;
  createdDate: string;
  createdBy: string | null;
  modifiedDate: string | null;
  modifiedBy: string | null;
}

/** Write payload shared by create and update. */
export interface SafetyMessageInput {
  payPeriodID: number;
  title: string;
  title_es: string | null;
  body: string;
  body_es: string | null;
}

/** Column widths in dbo.SafetyMessage, which are also the API's validation limits. */
export const SAFETY_TITLE_MAX = 200;
export const SAFETY_BODY_MAX = 2000;

/**
 * The whole list, one call.
 *
 * `from`/`to` are NOT optional in practice. The API defaults an omitted window to
 * eight weeks either side of today (db/safetyMessageUtil.ts DEFAULT_WINDOW_WEEKS),
 * which would silently hide any message written further ahead than that — and
 * writing next quarter's message early is a thing this page is for. Always pass
 * the full span of the tenant's pay periods.
 *
 * The window matches pay periods that OVERLAP [from, to], so boundary weeks are
 * included rather than clipped.
 */
export const getSafetyMessages = async (from: string, to: string): Promise<SafetyMessage[]> => {
  const response = await client.get('/admin/safetyMessages', { params: { from, to } });
  return response.data as SafetyMessage[];
};

export const createSafetyMessage = async (input: SafetyMessageInput): Promise<SafetyMessage> => {
  const response = await client.post('/admin/safetyMessages', input);
  return response.data as SafetyMessage;
};

/** Partial update — only the fields present are written. */
export const updateSafetyMessage = async (
  id: number,
  input: Partial<SafetyMessageInput>
): Promise<SafetyMessage> => {
  const response = await client.put(`/admin/safetyMessages/${id}`, input);
  return response.data as SafetyMessage;
};

/** Soft delete. The week goes back to "no message" and can be re-authored. */
export const deleteSafetyMessage = async (id: number): Promise<void> => {
  await client.delete(`/admin/safetyMessages/${id}`);
};

/** Server `message` when there is one, else the caller's fallback. */
export function getSafetyMessageErrorMessage(
  error: unknown,
  fallback = "Couldn't save changes. Try again."
): string {
  const msg = (error as any)?.response?.data?.message;
  if (typeof msg === 'string' && msg.trim() !== '') return msg;
  return fallback;
}

/** HTTP status, so the caller can route 409 to an inline error rather than a toast. */
export function getSafetyMessageErrorStatus(error: unknown): number | undefined {
  return (error as any)?.response?.status;
}
